import { Env } from '@contexts/AppContext';
import { Page } from '@tracking';
import { User } from '@utils/backend-api/currentUser';
import { FeedContentType } from '@utils/enums';

import { Author } from './authors';

export const MIN_COMMENTS_COUNT = 3;

export interface Advertisement {
  adScheduleId: number;
  clickthroughUrl: string;
  contentType: 'Advertisement';
  header: string;
  id: number;
  shortHeader?: string;
  sponsorLogo: string;
  sponsorName: string;
  text: string;
  trackingPixelUrl: string;
}

export interface AdvertisementBanner extends Advertisement {
  adSection: string;
  currentPage: Page;
  isContentPaywalled: boolean;
  shouldShowScrolledNav: boolean;
}

export interface Article {
  addendum?: string;
  advertisement?: Advertisement;
  authors: Author[];
  commentsCount?: number;
  commenters?: string[];
  contentType: 'Article';
  excerpt?: {
    long: string;
    short: string;
  };
  freeBlurb: string;
  featuredEntityTopic?: string;
  featuredGraphic?: string;
  frontCoverPhoto?: string;
  fullText: string | null;
  highlight: string;
  id: number;
  isFreeForKnownLeads: boolean;
  isPro: boolean;
  media?: Media;
  newsletterSlug?: string;
  pickups?: Pickup[];
  picture?: string;
  pictureCaption?: string;
  pictureLink?: string;
  publishedAt: string;
  publishedAtIso?: string;
  scriptList?: ScriptList[];
  skipPictureOptimization?: boolean;
  slug: string;
  subTitle?: string;
  takeaway?: ParsedTakeaway[];
  title: string;
  posterPicture?: string;
}

export interface ParsedTakeaway {
  type: string;
  content?: string;
  items?: string[];
}

export interface ScriptList {
  attributes: ScriptAttribute[];
  children?: string;
}

interface ScriptAttribute {
  name: string;
  value: string;
}

export interface ArticlePickup {
  slug: string;
  title: string;
  pickups: Omit<Pickup, 'author'>[];
}

export type MostPopularArticle = Pick<
  Article,
  'id' | 'title' | 'slug' | 'highlight' | 'publishedAt' | 'newsletterSlug'
>;

export interface Briefing {
  authors: Author[];
  body?: string;
  contentType?: 'Briefing';
  commentsCount?: number;
  dek: string;
  headline: string;
  id: number;
  publishedAt: string;
  slug: string;
}

export interface TITVStream {
  contentType: FeedContentType.TITVStream;
  coverUrl: string;
  guid: string;
  id: number;
  title: string;
  datetime: string;
}

export interface VideoUploadSegment {
  contentType: FeedContentType.VideoUpload;
  coverUrl?: string;
  publishedAtIso: string;
  guid: string;
  id: number;
  slug?: string;
  title?: string;
  videoType: string;
}

export interface CommunityComment {
  author: Author;
  excerpt: string;
  id: number;
  title: string;
  url: string;
}

export interface FeaturedPartner {
  name: string;
  tagline: string;
  targetLink: string;
}

export interface FeedUnit {
  content: ForumPost[] | (Article | Advertisement)[];
  unitDescription?: string;
  unitName: UnitName;
  viewAllUrl: string;
}

export type FeedUnits = {
  [slotIndex: number]: FeedUnit;
};

export interface ForumPost {
  id: number;
  title: string;
  author: Author;
}

export interface HomeProps {
  briefingNewsletter: Newsletter;
  communityComments: CommunityComment[];
  env: Env;
  featuredArticles?: Article[];
  feedContent: Article[] | (Article | Briefing | TITVStream | VideoUploadSegment)[];
  feedUnits?: FeedUnits;
  featuredPartners?: FeaturedPartner[];
  initialCurrentUser?: User;
  isGdprRequired: boolean;
  isNativeApp: boolean;
  isWebview: boolean;
  latestProArticles: LatestProArticles[];
  nativeAppVersion?: string;
  page?: number;
  recommendedArticles?: Article[];
  showBookmarksFlagEnabled?: boolean;
  sideFeedAdvertisement: Advertisement;
  upcomingEvents: UpcomingEvent[];
  view: string;
}

export type LatestProArticles = Pick<
  Article,
  'title' | 'slug' | 'highlight' | 'publishedAt' | 'newsletterSlug'
>;

export interface Media {
  description: string;
  embed_code?: string;
  id: number;
  img?: {
    url?: string;
  };
  preview: string;
  public: boolean;
  theme: string;
  title: string;
  video?: {
    url?: string;
  };
}

export interface Newsletter {
  author: string;
  contentType: FeedContentType.Newsletter;
  description: string;
  id: number;
  isLoggedIn: boolean;
  isSubscribed: boolean;
  name: string;
}

export interface NewsletterCard {
  author: Author;
  cardImageUrl?: string;
  description: string;
  frequency: string;
  name: string;
  id: number;
  slug: string;
}

export interface Pickup {
  author?: string;
  id: number;
  publication?: string;
  title: string;
  url: string;
  description?: string;
}

export enum UnitName {
  AIPromo = 'AI Promo',
  Forum = 'Trending on forum',
  FeaturedNewsletters = 'Featured Newsletters',
  TheWeekend = 'The Weekend',
}

export interface UpcomingEvent {
  slug: string;
  name: string;
  location: string;
  date: string;
}
